import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { cn } from "@src/lib/utils/cn";
import XIcon from "@src/lib/icons/12px/x";
import Text from "@src/lib/ui/Text";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

/**
 * Pulls the Tailwind `z-*` utility out of a resolved className.
 *
 * A dialog's scrim has to sit on the same stacking layer as the panel it dims.
 * Callers routinely raise only `DialogContent` to clear app chrome (the builder's
 * floating toolbar is `z-[750]`), which used to leave the overlay behind on the
 * default `z-50` — so that chrome kept rendering undimmed on top of the backdrop
 * while the panel floated above it. Forwarding the content's layer to the overlay
 * makes the pair move together, whatever a caller picks. See OPUS-17424.
 *
 * `className` is already run through `cn` (tailwind-merge) by the caller, so at
 * most one `z-*` utility survives here.
 */
const getZIndexClass = (className: string): string | undefined =>
  className.match(/(?:^|\s)(-?z-(?:\[[^\]\s]+\]|\d+))(?=\s|$)/)?.[1];

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-gray-alpha-9  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
    /** When true, omit the default sr-only title so the dialog can render a visible `DialogTitle` (only one `DialogPrimitive.Title` may exist). */
    hideDefaultTitle?: boolean;
  }
>(
  (
    {
      className,
      children,
      hideCloseButton = false,
      hideDefaultTitle = false,
      ...props
    },
    ref,
  ) => {
    const contentClassName = cn(
      "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg",
      className,
    );

    return (
      <DialogPortal>
        <DialogOverlay className={getZIndexClass(contentClassName)} />
        <DialogPrimitive.Content
          ref={ref}
          data-testid="dialog-content"
          onClick={(e) => e.stopPropagation()}
          className={contentClassName}
          {...props}
        >
          {!hideDefaultTitle && (
            <DialogPrimitive.Title className="sr-only">
              Dialog
            </DialogPrimitive.Title>
          )}
          <DialogPrimitive.Description className="sr-only">
            Dialog content
          </DialogPrimitive.Description>
          {children}
          {!hideCloseButton && (
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-gray-1 data-[state=open]:text-gray-13">
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = ({
  children,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title asChild>
    <Text variant="body-title" multiline className={className} {...props}>
      {children}
    </Text>
  </DialogPrimitive.Title>
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
  children,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description asChild>
    <Text
      variant="compact-description"
      multiline
      className={cn("text-muted-foreground", className)}
      {...props}
    >
      {children}
    </Text>
  </DialogPrimitive.Description>
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogSidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof ScrollableFlexGrow>
>(({ className, ...props }, ref) => (
  <ScrollableFlexGrow
    ref={ref}
    flex="none"
    className={cn("w-[280px] shrink-0 border-r border-gray-3", className)}
    innerClassName="p-[24px]"
    {...props}
  />
));
DialogSidebar.displayName = "DialogSidebar";

const DialogBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-1 self-stretch flex-col bg-gray-1", className)}
    {...props}
  />
));
DialogBody.displayName = "DialogBody";

const DialogBodyHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex h-[54px] shrink-0 items-center justify-between border-b border-gray-3 bg-white py-[10px]",
      className,
    )}
    {...props}
  />
));
DialogBodyHeader.displayName = "DialogBodyHeader";

const DialogTabs = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const [hasOverflow, setHasOverflow] = React.useState(false);

  const innerRef = React.useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const check = () => setHasOverflow(el.scrollLeft > 0);
    check();
    el.addEventListener("scroll", check, { passive: true });
    const observer = new ResizeObserver(check);
    observer.observe(el);
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        "relative min-w-0 flex-1 self-stretch border-r mr-2",
        hasOverflow ? "border-gray-2" : "border-transparent",
        className,
      )}
      {...props}
    >
      <div
        ref={innerRef}
        className="absolute inset-0 flex items-center gap-[32px] overflow-x-auto px-[24px]"
      >
        {children}
      </div>
    </div>
  );
});
DialogTabs.displayName = "DialogTabs";

const DialogTab = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    isActive?: boolean;
    children: React.ReactNode;
  }
>(({ className, isActive, children, ...props }, ref) => (
  <Text
    ref={ref}
    variant="compact-title"
    className={cn(
      "cursor-pointer whitespace-nowrap transition-all hover:text-gray-12",
      isActive ? "text-gray-12" : "text-gray-6",
      className,
    )}
    {...props}
  >
    {children}
  </Text>
));
DialogTab.displayName = "DialogTab";

const DialogActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex shrink-0 items-center justify-end gap-[12px] pr-[24px]",
      className,
    )}
    {...props}
  >
    {children}
  </div>
));
DialogActions.displayName = "DialogActions";

const DialogBodyScrollarea = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof ScrollableFlexGrow>
>(({ className, ...props }, ref) => (
  <ScrollableFlexGrow ref={ref} className={cn(className)} {...props} />
));
DialogBodyScrollarea.displayName = "DialogBodyScrollarea";

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogSidebar,
  DialogBody,
  DialogBodyHeader,
  DialogTabs,
  DialogTab,
  DialogActions,
  DialogBodyScrollarea,
};
